// frontend/src/features/agency/BannedDriverList.js
// This React component shows the agency’s banned drivers list, loading banned drivers from the API, syncing status changes in real time via Socket.IO, and reflecting unban/delete updates in Redux.
import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  upsertDriver,
  upsertDrivers,
  removeDriver,
  selectDriversByStatus,
  applyStatusChange,   // ✅ thunk
} from '../../features/drivers/driverSlice';
import { fetchDrivers } from '../../services/driverService';
import socket from '../../services/socket';

export default function BannedDriverList() {
  const drivers = useSelector(selectDriversByStatus('Banned'));
  const dispatch = useDispatch();

  // Initial load of banned drivers
  useEffect(() => {
    (async () => {
      try {
        const data = await fetchDrivers({ status: 'Banned' }); // ✅ correct call shape
        dispatch(upsertDrivers(data));
      } catch (e) {
        console.error('[BannedDriverList] fetch failed', e);
      }
    })();
  }, [dispatch]);

  // Real-time socket handlers
  useEffect(() => {
    const handleBan = payload => {
      // If server sends full driver, upsert; otherwise just flip status
      if (payload && (payload.name || payload.status)) {
        dispatch(upsertDriver(payload));
      } else {
        const id = payload.id ?? payload.driver_id;
        dispatch(applyStatusChange({ id, status: 'Banned' }));
      }
    };

    const handleUnban = payload => {
      const id = payload.id ?? payload.driver_id;
      // Use the status your flow returns to (Reserved or Active)
      dispatch(applyStatusChange({ id, status: 'Reserved' }));
    };

    const handleDelete = ({ id }) => dispatch(removeDriver(id));

    socket.on('driverBanned',   handleBan);
    socket.on('driverUnbanned', handleUnban);
    socket.on('driverDeleted',  handleDelete);

    return () => {
      socket.off('driverBanned',   handleBan);
      socket.off('driverUnbanned', handleUnban);
      socket.off('driverDeleted',  handleDelete);
    };
  }, [dispatch]);

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full bg-white rounded-lg shadow overflow-hidden">
        <thead className="bg-gray-50">
          <tr>
            {['Name', 'Vehicle Reg', 'Status', 'Reason'].map(col => (
              <th
                key={col}
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase"
              >
                {col}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {drivers.map(driver => (
            <tr key={driver.id} className="hover:bg-gray-50">
              <td className="px-6 py-4">{driver.name}</td>
              <td className="px-6 py-4">{driver.vehicle_registration_number}</td>
              <td className="px-6 py-4 text-red-600">{driver.status}</td>
              <td className="px-6 py-4 text-gray-700">
                {driver.ban_reason || '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
